import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../api/axiosInstance';

export const WIZARD_STEPS = ['organization', 'employees', 'warehouses', 'inventory', 'contacts'] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

export interface OnboardingWizardStatus {
  wizardSteps: Record<string, boolean>;
  wizardCompleted: boolean;
  wizardDismissed: boolean;
}

const STORAGE_KEY = 'bizcore-wizard-progress';

function readLocal(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

function writeLocal(steps: Record<string, boolean>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(steps));
  } catch { /* ignore */ }
}

export function useWizardProgress() {
  const queryClient = useQueryClient();

  const { data: serverStatus, isLoading } = useQuery({
    queryKey: ['onboarding-wizard-status'],
    queryFn: async () => {
      const res = await axiosInstance.get('/onboarding/status');
      const d = res.data?.data;
      const wizardSteps: Record<string, boolean> = {};
      for (const step of WIZARD_STEPS) {
        wizardSteps[step] = Boolean(d?.wizardSteps?.[step]);
      }
      const status: OnboardingWizardStatus = {
        wizardSteps,
        wizardCompleted: Boolean(d?.wizardCompleted),
        wizardDismissed: Boolean(d?.wizardDismissed),
      };
      writeLocal(wizardSteps);
      return status;
    },
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const [localSteps, setLocalSteps] = useState<Record<string, boolean>>(readLocal);

  useEffect(() => {
    if (serverStatus?.wizardSteps) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional server→local sync for optimistic UI
      setLocalSteps((prev) => ({ ...prev, ...serverStatus.wizardSteps }));
    }
  }, [serverStatus?.wizardSteps]);

  const completedSteps = useMemo(() => {
    const merged = { ...localSteps, ...serverStatus?.wizardSteps };
    return WIZARD_STEPS.filter((s) => merged[s]);
  }, [localSteps, serverStatus]);

  const currentStepIndex = completedSteps.length < WIZARD_STEPS.length
    ? completedSteps.length
    : WIZARD_STEPS.length - 1;

  const completeStepMutation = useMutation({
    mutationFn: async (step: WizardStep) => {
      const res = await axiosInstance.post('/onboarding/complete-step', { step });
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-wizard-status'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post('/onboarding/dismiss');
      return res.data?.data;
    },
    onMutate: () => {
      // Optimistically flip wizardDismissed so RequireWizard doesn't bounce /dashboard back to
      // /setup in the window before the POST lands (navigate() runs synchronously after dismiss()).
      queryClient.setQueryData<OnboardingWizardStatus | undefined>(
        ['onboarding-wizard-status'],
        (old) => (old ? { ...old, wizardDismissed: true } : old),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-wizard-status'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
    retry: 1,
    retryDelay: 300,
  });

  const completeStep = useCallback((step: WizardStep) => {
    const local = readLocal();
    local[step] = true;
    writeLocal(local);
    queryClient.setQueryData<OnboardingWizardStatus | undefined>(
      ['onboarding-wizard-status'],
      (old) => old
        ? { ...old, wizardSteps: { ...old.wizardSteps, [step]: true } }
        : old,
    );
    completeStepMutation.mutate(step);
  }, [completeStepMutation, queryClient]);

  const dismiss = useCallback(() => {
    dismissMutation.mutate();
  }, [dismissMutation]);

  return {
    steps: WIZARD_STEPS,
    completedSteps,
    currentStep: WIZARD_STEPS[currentStepIndex],
    currentStepIndex,
    isCompleted: serverStatus?.wizardCompleted ?? false,
    isDismissed: serverStatus?.wizardDismissed ?? false,
    completeStep,
    dismiss,
    isLoading,
  };
}
